import React from 'react';
import { motion, useInView } from 'framer-motion';
import { RevealWords } from '../motion/RevealText';

const CHAPTERS = [
    {
        n: '02',
        overline: 'Philosophy',
        title: 'Paint is architecture.',
        color: 'text-brand-blue',
        body:
            'Every wall we touch carries decades of decisions — mineral, humidity, light. We treat preparation as design work: taping is drafting, priming is composition, the final coat is signature.',
    },
    {
        n: '03',
        overline: 'Material',
        title: 'Colour, chosen with quiet.',
        color: 'text-brand-red',
        body:
            'We source premium, low-VOC finishes and specify them against the New Mexico light. Adobe warmth in shade, restraint in direct sun. A palette that reads as still, considered, expensive.',
    },
    {
        n: '04',
        overline: 'Method',
        title: 'One crew, one job. No shortcuts.',
        color: 'text-brand-blue',
        body:
            'A single, dedicated team walks a home from first tape to final touch-up. No sub-contracted rotation. No rushed corners. The finish is the sum of a thousand small decisions in a row.',
    },
];

const Chapter = ({ ch, index }) => {
    const ref = React.useRef(null);
    const inView = useInView(ref, { once: true, amount: 0.3 });
    const alignRight = index % 2 === 1;
    return (
        <div
            ref={ref}
            className="relative grid grid-cols-12 gap-6 md:gap-10 py-24 md:py-40 border-t border-white/10 md:items-center"
        >
            {/* Numeral */}
            <motion.div
                initial={{ opacity: 0, y: 40 }}
                animate={inView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 1.2, ease: [0.7, 0, 0.2, 1] }}
                className={`col-span-12 md:col-span-4 ${alignRight ? 'md:order-2' : ''}`}
            >
                <div className="chapter-numeral text-[22vw] md:text-[16vw] lg:text-[14rem] text-transparent leading-[0.8]" style={{ WebkitTextStroke: '1px rgba(244,240,234,0.35)' }}>
                    {ch.n}
                </div>
            </motion.div>

            {/* Copy */}
            <div className={`col-span-12 md:col-span-7 md:col-start-6 ${alignRight ? 'md:col-start-1 md:order-1' : ''}`}>
                <div className="eyebrow mb-5 text-bone-400">Chapter {ch.n} · {ch.overline}</div>
                <motion.h3
                    initial={{ opacity: 0, y: 30 }}
                    animate={inView ? { opacity: 1, y: 0 } : {}}
                    transition={{ duration: 1, delay: 0.15 }}
                    className={`font-display text-4xl md:text-6xl lg:text-7xl font-light leading-[1.02] tracking-tight ${ch.color || 'text-bone-100'} max-w-3xl`}
                >
                    {ch.title}
                </motion.h3>
                <div className="mt-8 max-w-xl">
                    <RevealWords
                        text={ch.body}
                        className="font-body text-lg font-light leading-relaxed text-bone-300"
                    />
                </div>
            </div>
        </div>
    );
};

export const Manifesto = () => {
    return (
        <section
            id="craft"
            data-testid="msd-manifesto"
            className="relative z-20 bg-ink-900 px-6 md:px-10"
        >
            <div className="mx-auto max-w-[1600px]">
                <div className="pt-24 md:pt-32 pb-8 flex items-center gap-4">
                    <span className="w-10 h-px bg-adobe-500" />
                    <span className="eyebrow">Manifesto</span>
                </div>
                {CHAPTERS.map((ch, i) => (
                    <Chapter ch={ch} index={i} key={ch.n} />
                ))}
            </div>
        </section>
    );
};
